import { DirectoryProfessionals } from '@platform/client';
import { LogoTopPanel } from '../LogoTopPanel';
import { useParams } from 'wouter';
import IntroductionCTA from '../IntroductionCTA';
import { DirectoryInnerSection } from '../ContentSection';
import { getLinkedInLink, InfoCard, InfoCardItem } from '../InfoCard';
import { SubheaderPanel } from '../SubheaderPanel';
import { PillsList } from '../PillsList';
import { DirectoryLayout } from '../DirectoryLayout';
import { DirectoryOuterSection } from '../DirectoryContentGrid';
import { useEffect, useState } from 'react';
import { DescriptionText } from '../DescriptionText';
import { JoinAlert } from '../JoinAlert';
import MetaTags from '../../util/MetaTags';
import { DirectoryPageContext } from '../DirectoryPageContext';
import IntroductionStatusText, { isIntroStatusTextVisible } from '../IntroductionStatusText';
import Skeleton from 'react-loading-skeleton';
import { ProCohortAvailabilityText } from '../ProCohortAvailibilityText';

const formatHeadline = (
  jobTitle: string | null,
  yearsOfExperience: string | null,
  employer: string | null,
): string => {
  // All three present
  if (jobTitle && employer && yearsOfExperience) {
    return `${jobTitle} at ${employer} with ${yearsOfExperience} years of experience`;
  }
  // jobTitle and employer
  if (jobTitle && employer && !yearsOfExperience) {
    return `${jobTitle} at ${employer}`;
  }
  // jobTitle and yearsOfExperience
  if (jobTitle && !employer && yearsOfExperience) {
    return `${jobTitle} with ${yearsOfExperience} years of experience`;
  }
  // employer and yearsOfExperience
  if (!jobTitle && employer && yearsOfExperience) {
    return `Employed at ${employer} with ${yearsOfExperience} years of experience`;
  }
  // Only jobTitle
  if (jobTitle && !employer && !yearsOfExperience) {
    return jobTitle;
  }
  // Only employer
  if (!jobTitle && employer && !yearsOfExperience) {
    return `Employed at ${employer}`;
  }
  // Only yearsOfExperience
  if (!jobTitle && !employer && yearsOfExperience) {
    return `${yearsOfExperience} years of experience`;
  }
  // None present
  return '';
};

const ProfessionalProfile = () => {
  const { userId } = useParams<{ userId: string }>();
  const { isSuccess: isSuccess, data: user } = DirectoryProfessionals.useGetProfessionalById(
    parseInt(userId),
  );
  const [infoCardItems, setInfoCardItems] = useState<InfoCardItem[]>([]);
  const proName = user ? `${user?.firstName} ${user?.lastName}` : '';

  useEffect(() => {
    if (!isSuccess || !user) {
      return;
    }

    const items: InfoCardItem[] = [];

    if (user?.location?.city?.length || user?.location?.country?.length) {
      items.push({
        type: 'location',
        city: user?.location?.city ?? '',
        country: user?.location?.country ?? '',
        countryCode: user?.location?.countryCode ?? undefined,
      });
    }

    if (user?.socialMedia.linkedin) {
      items.push({
        type: 'linked-in',
        accountName: getLinkedInLink(user.socialMedia.linkedin),
      });
    }

    if (user?.resume) {
      items.push({ type: 'resume', url: user.resume });
    }

    // Disabled for now per Product, leaving as it may be re-enabled before deploy
    // if (user?.socialMedia.website) {
    //   items.push({ type: 'website', url: user.socialMedia.website });
    // }

    setInfoCardItems(items);
  }, [isSuccess, user]);

  if (!isSuccess) {
    return (
      <DirectoryLayout>
        <LogoTopPanel avatarFallback={{ fallbackIcon: 'user-01' }} isAvatar>
          <div className='flex flex-col items-center'>
            <Skeleton width={400} />
            <Skeleton width={300} />
          </div>
        </LogoTopPanel>
        <DirectoryOuterSection>
          <DirectoryInnerSection title='About'>
            <div className='rounded-xl bg-gray-50 px-6 py-5 border border-gray-100 flex flex-col items-baseline sm:flex-row sm:flex-wrap sm:justify-between gap-6 sm:gap-0'>
              <Skeleton width={200} />
              <Skeleton width={200} />
              <Skeleton width={200} />
            </div>
            <Skeleton width={600} />
            <SubheaderPanel title='Primary skills'>
              <div className='flex gap-2'>
                <Skeleton width={100} />
                <Skeleton width={100} />
              </div>
            </SubheaderPanel>
            <SubheaderPanel title='Main industries'>
              <div className='flex gap-2'>
                <Skeleton width={100} />
                <Skeleton width={100} />
                <Skeleton width={100} />
              </div>
            </SubheaderPanel>
            <SubheaderPanel title="Impact goals I'm interested in">
              <div className='flex gap-2'>
                <Skeleton width={100} />
                <Skeleton width={100} />
              </div>
            </SubheaderPanel>
            <SubheaderPanel title='Languages I speak'>
              <div className='flex gap-2'>
                <Skeleton width={100} />
                <Skeleton width={100} />
                <Skeleton width={100} />
              </div>
            </SubheaderPanel>
          </DirectoryInnerSection>
        </DirectoryOuterSection>
      </DirectoryLayout>
    );
  }

  const headline = formatHeadline(
    user?.positionTitle,
    user?.experienceYears?.toString() ?? null,
    user?.employer,
  );
  let languages = [...(user?.fluentLanguages ?? []), ...(user?.otherLanguages ?? [])];
  if (!languages.length) {
    languages = ['English'];
  }

  return (
    <DirectoryLayout>
      <DirectoryPageContext.Provider
        value={{
          targetUserId: parseInt(userId),
          sendingFromRole: 'REPRESENTATIVE',
          queryKey: DirectoryProfessionals.getGetProfessionalByIdQueryKey(parseInt(userId)),
        }}
      >
        <MetaTags
          title={`${user?.firstName} on MovingWorlds.org`}
          description={`${user?.firstName} on MovingWorlds.org`}
        />
        <LogoTopPanel
          avatarUrl={user?.avatar ?? undefined}
          avatarFallback={{ name: proName }}
          isAvatar
        >
          <div className='flex flex-col items-center'>
            <div className='text-xl sm:text-2xl font-semibold sm:mb-1'>{proName}</div>
            {headline && <div className='text-base text-gray-500'>{headline}</div>}
            <div className='my-4'>
              {user?.canSendIntro && (
                <IntroductionCTA
                  apiCtaState={user.introCtaState}
                  recipientName={proName}
                  requireProjectSelection
                  otherPartyType='professional'
                />
              )}
            </div>
            {isIntroStatusTextVisible(user?.introCtaState.state) && (
              <div className='mb-3'>
                <IntroductionStatusText introCtaState={user.introCtaState} />
              </div>
            )}
          </div>
        </LogoTopPanel>
        <DirectoryOuterSection>
          <DirectoryInnerSection title='About'>
            {infoCardItems.length > 0 && <InfoCard items={infoCardItems} />}
            {user?.cohort && <ProCohortAvailabilityText cohort={user.cohort} />}
            {user?.headline?.length && <DescriptionText html={user?.headline} />}
            {!!user?.skills.length && (
              <SubheaderPanel title='Primary skills'>
                <PillsList items={user?.skills.map((skill) => skill) || []} />
              </SubheaderPanel>
            )}
            {!!user?.industries.length && (
              <SubheaderPanel title='Main industries'>
                <PillsList items={user?.industries} />
              </SubheaderPanel>
            )}
            {!!user?.preferredCauses.length && (
              <SubheaderPanel title="Impact goals I'm interested in">
                <PillsList items={user?.preferredCauses.sort().map((cause) => cause) || []} />
              </SubheaderPanel>
            )}
            <SubheaderPanel title='Languages I speak'>
              <PillsList items={languages.map((lang) => lang) || []} />
            </SubheaderPanel>
            {!user?.currentUserIsAuthed && <JoinAlert />}
          </DirectoryInnerSection>
        </DirectoryOuterSection>
      </DirectoryPageContext.Provider>
    </DirectoryLayout>
  );
};

export default ProfessionalProfile;
